import { useEffect, useState } from 'react';
import { Download, XCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface SettingsResponse {
  success: boolean;
  settings: {
    appDownloadEnabled: boolean;
  };
}

type PageState = 'loading' | 'enabled' | 'disabled' | 'error';

export default function DownloadPage() {
  const [pageState, setPageState] = useState<PageState>('loading');
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch('/api/settings');
      const data: SettingsResponse = await response.json();
      
      if (data.success) {
        setPageState(data.settings.appDownloadEnabled ? 'enabled' : 'disabled');
      } else {
        setPageState('error');
      }
    } catch {
      setPageState('error');
    }
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    
    try {
      const response = await fetch('/api/app/download');
      
      if (!response.ok) {
        throw new Error('Download failed');
      }

      // Get the filename from content-disposition header
      const contentDisposition = response.headers.get('content-disposition');
      const filenameMatch = contentDisposition?.match(/filename="?([^";\n]+)"?/);
      const filename = filenameMatch?.[1] || 'ping-mr-darling.apk';

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      // If download fails, try direct navigation as fallback
      window.location.href = '/api/app/download';
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-violet-900/20 via-background to-background">
      {/* Decorative elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-fuchsia-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md space-y-8 animate-in">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="w-24 h-24 mx-auto rounded-3xl overflow-hidden shadow-2xl shadow-violet-500/25">
            <img src="/app-icon.png" alt="Ping Mr Darling" className="w-full h-full object-cover" />
          </div>
          <h1 className="text-4xl font-display font-bold bg-gradient-to-r from-violet-400 via-fuchsia-400 to-pink-400 bg-clip-text text-transparent">
            Ping Mr Darling
          </h1>
          <p className="text-muted-foreground text-lg">
            Android App
          </p>
        </div>

        {/* Content based on state */}
        <div className="glass rounded-2xl p-8 space-y-6">
          {pageState === 'loading' && (
            <div className="flex flex-col items-center gap-4 py-8">
              <Loader2 className="w-10 h-10 text-violet-400 animate-spin" />
              <p className="text-muted-foreground">Checking availability...</p>
            </div>
          )}

          {pageState === 'enabled' && (
            <>
              <div className="text-center space-y-2">
                <p className="text-foreground">
                  Get the app to share photos, videos, and messages directly to the family display.
                </p>
              </div>

              <Button
                onClick={handleDownload}
                disabled={isDownloading}
                className="w-full h-16 text-lg font-semibold bg-gradient-to-r from-violet-600 via-fuchsia-600 to-pink-600 hover:from-violet-500 hover:via-fuchsia-500 hover:to-pink-500 transition-all duration-300 shadow-lg shadow-fuchsia-500/25 hover:shadow-fuchsia-500/40 hover:scale-[1.02] active:scale-[0.98]"
                size="lg"
              >
                {isDownloading ? (
                  <>
                    <Loader2 className="w-6 h-6 mr-3 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="w-6 h-6 mr-3" />
                    Download App
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                Requires Android 7.0 or later
              </p>
            </>
          )}

          {pageState === 'disabled' && (
            <div className="flex flex-col items-center gap-4 py-4 text-center">
              <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center">
                <XCircle className="w-8 h-8 text-muted-foreground" />
              </div>
              <div className="space-y-2">
                <p className="text-foreground font-medium">
                  Downloads Unavailable
                </p>
                <p className="text-muted-foreground text-sm">
                  App downloads are currently disabled. Please contact Ronny if you want access.
                </p>
              </div>
            </div>
          )}

          {pageState === 'error' && (
            <div className="flex flex-col items-center gap-4 py-4 text-center">
              <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
                <XCircle className="w-8 h-8 text-destructive" />
              </div>
              <div className="space-y-2">
                <p className="text-foreground font-medium">
                  Something went wrong
                </p>
                <p className="text-muted-foreground text-sm">
                  Could not check download availability. Please try again later.
                </p>
              </div>
              <Button
                variant="secondary"
                onClick={() => {
                  setPageState('loading');
                  fetchSettings();
                }}
              >
                Try Again
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-xs text-center text-muted-foreground/60">
          Ping Mr Darling &middot; Family Display App
        </p>
      </div>
    </div>
  );
}

